import React, { useEffect, useState, useCallback } from "react";
import {
  TextField, MenuItem, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton, Tooltip, Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import RefreshIcon from "@material-ui/icons/Refresh";
import HistoryIcon from "@material-ui/icons/History";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  when: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: theme.palette.text.secondary },
  actionsCell: { display: "flex", gap: 4, flexWrap: "wrap" },
  error: {
    color: theme.palette.error.main,
    maxWidth: 240,
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // O <pre> usava #f7f7f7 fixo: no tema escuro virava texto claro sobre
  // fundo claro, ilegível justamente onde se vai ler o payload de um erro.
  pre: {
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    fontSize: 12,
    maxHeight: 220,
    overflow: "auto",
    margin: `${theme.spacing(0.5)}px 0 0`,
  },
  detailRow: { marginBottom: 4 },
}));

const formatDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch (e) { return s; }
};

const AutomationLogs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [autos, setAutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ automationId: "", success: "", limit: 100 });
  const [view, setView] = useState({ open: false, log: null });

  const loadAutos = useCallback(async () => {
    try {
      const { data } = await api.get("/automations");
      setAutos(Array.isArray(data) ? data : []);
    } catch (err) { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: filter.limit };
      if (filter.automationId) params.automationId = filter.automationId;
      if (filter.success !== "") params.success = filter.success;
      const { data } = await api.get("/automations/logs", { params });
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) { toast.error("Erro ao listar logs"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { loadAutos(); }, [loadAutos]);
  useEffect(() => { load(); }, [load]);

  const automationName = (id) => {
    const a = autos.find(x => x.id === id);
    return a ? a.name : `#${id}`;
  };

  const columns = [
    { key: "triggeredAt", label: "Quando", render: l => <span className={classes.when}>{formatDate(l.triggeredAt)}</span> },
    {
      key: "automation",
      label: "Automação",
      render: l => <Typography variant="subtitle2">{automationName(l.automationId)}</Typography>,
    },
    { key: "trigger", label: "Gatilho", render: l => <StatusChip tone="neutral" label={l.trigger} /> },
    {
      key: "success",
      label: "Resultado",
      render: l => (
        <StatusChip dot tone={l.success ? "success" : "danger"} label={l.success ? "Sucesso" : "Falha"} />
      ),
    },
    {
      key: "actionsExecuted",
      label: "Ações executadas",
      render: l => {
        const acts = l.actionsExecuted || [];
        if (!acts.length) return "—";
        return (
          <div className={classes.actionsCell}>
            {acts.map((a, i) => (
              <StatusChip key={i} tone={a.ok ? "success" : "danger"} label={a.type} />
            ))}
          </div>
        );
      },
    },
    {
      key: "error",
      label: "Erro",
      render: l => (l.error ? <span className={classes.error} title={l.error}>{l.error}</span> : "—"),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: l => (
        <Tooltip title="Ver payload e ações">
          <IconButton size="small" onClick={() => setView({ open: true, log: l })}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <MainContainer>
      <PageHeader
        title="Logs de automação"
        count={logs.length}
        subtitle="Cada disparo de automação, com o payload que chegou e o que cada ação fez."
        actions={
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Atualizar
          </Button>
        }
        filters={
          <>
            <TextField select label="Automação" style={{ minWidth: 220 }} value={filter.automationId}
              onChange={(e) => setFilter(f => ({ ...f, automationId: e.target.value }))}>
              <MenuItem value="">Todas</MenuItem>
              {autos.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </TextField>
            <TextField select label="Resultado" style={{ minWidth: 140 }} value={filter.success}
              onChange={(e) => setFilter(f => ({ ...f, success: e.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Sucesso</MenuItem>
              <MenuItem value="false">Falha</MenuItem>
            </TextField>
            <TextField type="number" label="Limite" style={{ width: 100 }} value={filter.limit}
              onChange={(e) => setFilter(f => ({ ...f, limit: e.target.value }))} />
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={logs}
        loading={loading}
        emptyIcon={<HistoryIcon />}
        emptyTitle="Nenhum log no período"
        emptyDescription="Os logs aparecem assim que uma automação for disparada por um gatilho. Se já existem automações ativas, verifique os filtros."
      />

      <Dialog open={view.open} onClose={() => setView({ open: false, log: null })} fullWidth maxWidth="md">
        <DialogTitle>Detalhe do log</DialogTitle>
        <DialogContent dividers>
          {view.log && (
            <>
              <div className={classes.detailRow}><strong>Automação:</strong> {automationName(view.log.automationId)}</div>
              <div className={classes.detailRow}><strong>Gatilho:</strong> {view.log.trigger}</div>
              <div className={classes.detailRow}><strong>Quando:</strong> {formatDate(view.log.triggeredAt)}</div>
              <div className={classes.detailRow}>
                <strong>Resultado:</strong>{" "}
                <StatusChip dot tone={view.log.success ? "success" : "danger"}
                  label={view.log.success ? "Sucesso" : "Falha"} />
              </div>
              {view.log.error && (
                <div className={classes.detailRow} style={{ color: "inherit" }}>
                  <strong>Erro:</strong> <span className={classes.error} style={{ maxWidth: "none", whiteSpace: "normal" }}>{view.log.error}</span>
                </div>
              )}
              <Box mt={2}><strong>Payload</strong>
                <pre className={classes.pre}>
                  {JSON.stringify(view.log.payload || {}, null, 2)}
                </pre>
              </Box>
              <Box mt={2}><strong>Ações executadas</strong>
                <pre className={classes.pre}>
                  {JSON.stringify(view.log.actionsExecuted || [], null, 2)}
                </pre>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setView({ open: false, log: null })}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default AutomationLogs;
